/**
 * Via Simplicity (III)
 *
 * Interactive. Rotate and zoom with mouse or fingers. A series exploring how complex movement can arise from simple beginnings. Five concentric rings of energy pulse and rotate, all built up from the simple rotation of three idential objects around each other. Tested on a variety of modern browsers and devices, probably won't work on all. Sold "as is"
 * 
 */

import * as THREE from 'three/build/three.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import chroma from 'chroma-js';

import World from '../common/World';
import WorldConfig from '../common/WorldConfig';

// Check out roughness mipmapper, sounds cool
// https://stackoverflow.com/questions/60704912/play-a-gltf-animation-three-js


function convertHex(hex) {
	return parseInt(hex.replace(/^#/, ''), 16);
}


class Main extends World {

	constructor() {


		super({
			cameraType: 'orbit',
			cameraLightIntensity: 2500,
			ambientIntensity: .5,
			backgroundColor: 0x000000,
			useBloom: true,
			mutedTone: false,
			bloomIntensity: .75
		});

		const promises = [
			this.processGLTF(require('url:./cams_core.glb'), {
				addAnimations: false,			
				addObjects: false
			})
		];

		this.cameraControls.minDistance = 30;
		this.cameraControls.maxDistance = 55;
		this.cameraControls.distance = 40;

		this.cameraControls.maxPolarAngle = Infinity;
		this.cameraControls.minPolarAngle = -Infinity;

		this.cameraControls.truckSpeed = 0;

		this.cameraControls.mouseButtons.right = false;

		this.cams = [];
		this.allCams = [];

		this.radius = 25;
		this.total = 128;
		this.center = new THREE.Vector2(0, 0);
		this.duration = 5;

		const pivotMaterial = new THREE.MeshBasicMaterial({ colorWrite: false });


		const colors = [			
			['#E89005', '#EC7505', '#D84A05', '#F42B03', '#E70E02'],
			['#D36135', '#7FB069', '#ECE4B7', '#E6AA68', '#02020B'],
			['#42033D', '#680E4B', '#7C238C', '#854798', '#7C72A0'],
			['#03045e', '#0077b6', '#00b4d8', '#90e0ef', '#caf0f8'],
			['#5eff36', '#7fc8f8', '#cf23b8', '#dbfe87', '#2511a8'],
		];

		this.scales = [];

		this.frame = 0;

		this.mainScaleColors = [];

		for(let x = 0; x < colors[0].length; x++) {
			const scale = chroma.scale([colors[0][x], colors[1][x], colors[2][x], colors[3][x], colors[4][x]]);
			this.scales.push(scale);
			this.mainScaleColors.push(colors[0][x]);
		}

		this.levels = [
			{
				scale: 1				
			},
			{
				scale: 0.73
			},
			{
				scale: 0.5
			},
			{
				scale: 0.3
			},
			{
				scale: 0.2
			}
		];

		this.camPivots = [];

		Promise.all(promises).then(v => {

			const data = v[0];

			//this.scene.add(data.scene);

			const cams = data.scene.getObjectByName("Cams");

			if(cams) {

				this.levels.forEach((level, levelNumber) => {

					const pivotGeometry = new THREE.BoxGeometry( .01, .01, .01 );
					const pivot = new THREE.Mesh(pivotGeometry, pivotMaterial);
					this.scene.add(pivot);
					this.camPivots[levelNumber] = pivot;
					this.cams[levelNumber] = [];

					const radius = this.radius * level.scale;
					const total = this.total * level.scale;

					for(let x = 0; x < total; x++) {
						
						const cam = cams.clone(true);

						cam.levelScale = level.scale;

						const obj = cam.getObjectByName("Cam1");
						obj.material = obj.material.clone();
						cam.getObjectByName("Cam2").material = obj.material;
						cam.getObjectByName("Cam3").material = obj.material;
						obj.material.emissive = new THREE.Color(0xFFFFFF);
						obj.material.emissiveIntensity = 0;

						const mixer = new THREE.AnimationMixer(cam);

						data.animations.forEach(animation => {
							const action = mixer.clipAction(animation);
							action.play();
						});


						mixer.setTime(this.duration * (x / this.total));
						
						this.mixers.push(mixer);
				

						const theta = Math.PI * 2 * x / total;

						const _x = this.center.x + radius * Math.cos(theta);
						const _y = this.center.y + radius * Math.sin(theta);

						
						cam.position.set(0, _x, _y);
						cam.rotation.x = theta;

						this.camPivots[levelNumber].add(cam);

						cam.i = x;
						cam.pc = x / total;
						cam.level = levelNumber;
						
						this.allCams.push(cam);
						this.cams[levelNumber].push(cam);

						

					}
				});

			}


			const myobj = document.getElementById("loading");
			myobj.remove(); 



		});


		this.camera.near = 0.25;
		this.camera.far = 250;
		this.camera.updateProjectionMatrix();

		this.mixers = [];
	}


	renderLoop(t) {
		super.renderLoop(t);

		if(this.camPivots && this.camPivots.length > 0) {
			this.camPivots[0].rotation.z += this.delta * Math.PI / 16;
			this.camPivots[0].rotation.x += this.delta * Math.PI / 32;
			this.camPivots[1].rotation.y += this.delta * Math.PI / 6;
			this.camPivots[1].rotation.z += this.delta * Math.PI / 32;
			this.camPivots[2].rotation.x += this.delta * Math.PI / 5;
			this.camPivots[2].rotation.y += this.delta * Math.PI / 32;
			this.camPivots[3].rotation.z -= this.delta * Math.PI / 9;
			this.camPivots[3].rotation.x -= this.delta * Math.PI / 13;
			this.camPivots[4].rotation.x -= this.delta * Math.PI / 29;
			this.camPivots[4].rotation.y -= this.delta * Math.PI / 15;
			this.camPivots[4].rotation.z -= this.delta * Math.PI / 9;
		}

		this.frame++;


		if(this.frame % 5 == 0 || !this.mainScale) {

			let val = t / 60000;

			if(Math.floor(val) % 2 == 1) {
				val = (1 - val % 1);
			} else {
				val = val % 1;
			}

			this.mainScaleColors = [
				this.scales[0](val).hex(),
				this.scales[1](val).hex(),
				this.scales[2](val).hex(),
				this.scales[3](val).hex(),
				this.scales[4](val).hex()
			];


			this.mainScale = chroma.scale(this.mainScaleColors);
		}

		if(this.allCams) {
			this.allCams.forEach(cam => {

				let r;

				const obj = cam.getObjectByName("Cam1");

				let val = (t / 5000 + (cam.level / this.levels.length) + (cam.pc * 0.05));

				if(Math.floor(val) % 2 == 1) {
					val = (1 - val % 1);
				} else {
					val = val % 1;
				}

				let col = this.mainScale(val).hex().toString();
			

				obj.material.emissive.set(col);
				obj.material.color.set(col);

				if(cam.level == 0) {
					r = Math.abs(Math.sin((cam.i + t / 100) / this.total * Math.PI * 4)) * .5 + 1;
					obj.material.emissiveIntensity = Math.abs(Math.sin((cam.i + t / 100) / this.total * Math.PI * 4));
				} else if(cam.level == 1) {
					r = Math.abs(Math.sin((cam.i + t / 200) / this.total * Math.PI * 8 / cam.levelScale)) * .7 + .8;
					obj.material.emissiveIntensity = Math.abs(Math.sin((cam.i + t / 200) / this.total * Math.PI * 2 / cam.levelScale)) * .6;
				} else if(cam.level == 2) {
					r = Math.abs(Math.sin((cam.i + t / 100) / this.total * Math.PI * 16 / cam.levelScale)) * .3 + 2;
					obj.material.emissiveIntensity = Math.abs(Math.sin((cam.i + t / 400) / this.total * Math.PI / cam.levelScale)) * .5;
				} else if(cam.level == 3) {
					r = Math.abs(Math.sin((cam.i + t / 100) / this.total * Math.PI * 8 / cam.levelScale)) * .5 + 1.4;
					obj.material.emissiveIntensity = Math.abs(Math.sin((cam.i + t / 800) / this.total * Math.PI / cam.levelScale)) * .5;
				} else if(cam.level == 4) {
					r = Math.abs(Math.sin((cam.i + t / 200) / this.total * Math.PI * 4 / cam.levelScale)) * .3 + 3;
					obj.material.emissiveIntensity = Math.abs(Math.sin((cam.i + t / 1600) / this.total * Math.PI / cam.levelScale));
				}
				
				cam.scale.set(r * cam.levelScale, r * cam.levelScale, 1);
				cam.rotation.z = t / 1000;
			});
		}


	}


}

export default Main;